import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { orderBy, QueryConstraint, where } from "firebase/firestore";
import { FirestoreService } from "../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../api/firebase/references";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import { MedicalPaOrderStatus } from "../../../enums/medicalPaOrderStatus";
import { logDataToConsole } from "../../../utils/customLogger";

interface MedicalPaOrdersState {
  financialMedicalPaOrders: MedicalPaOrder[] | null;
  loading: boolean;
  error: string | null;
  lastVisible: any;
  showinLineLoader: boolean;
  totalDocuments: number;
  currentlyLoadedCount: number;
}

const initialState: MedicalPaOrdersState = {
  financialMedicalPaOrders: null,
  loading: false,
  error: null,
  lastVisible: null,
  showinLineLoader: false,
  totalDocuments: 0,
  currentlyLoadedCount: 0,
};

const financialMedicalPaOrdersSlice = createSlice({
  name: "financialMedicalPaOrders",
  initialState,
  reducers: {
    setFinancialMedicalPaOrders: (state, action) => {
      state.financialMedicalPaOrders = action.payload;
      state.currentlyLoadedCount = action.payload.length;
      state.loading = false;
      state.error = null;
    },

    appendFinancialMedicalPaOrders: (
      state,
      action: PayloadAction<MedicalPaOrder[]>,
    ) => {
      state.financialMedicalPaOrders = state.financialMedicalPaOrders
        ? [...state.financialMedicalPaOrders, ...action.payload]
        : action.payload;
      state.currentlyLoadedCount = state.financialMedicalPaOrders.length;
      state.loading = false;
      state.error = null;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },

    setLastVisible: (state, action: PayloadAction<any>) => {
      state.lastVisible = action.payload;
    },

    setTotalDocuments: (state, action: PayloadAction<number>) => {
      state.totalDocuments = action.payload;
    },

    resetPagination: (state) => {
      state.lastVisible = null;
      state.currentlyLoadedCount = 0;
      state.financialMedicalPaOrders = null;
    },
    setinLineLoader: (state, action: PayloadAction<boolean>) => {
      state.showinLineLoader = action.payload;
    },
  },
});

export const {
  setFinancialMedicalPaOrders,
  appendFinancialMedicalPaOrders,
  setLoading,
  setError,
  setLastVisible,
  setTotalDocuments,
  resetPagination,
  setinLineLoader,
} = financialMedicalPaOrdersSlice.actions;

export const fetchTotalDocumentsCount =
  (
    facilityId: string,
    providerId?: string,
    queryConstraints?: QueryConstraint[],
  ) =>
  async (dispatch) => {
    try {
      const allDocs =
        await FirestoreService.getDocumentsByQuery<MedicalPaOrder>(
          FirestoreCollectionReference.medicalPaOrders(),
          queryConstraints ?? [],
        );

      dispatch(setTotalDocuments(allDocs.length));
    } catch (error) {
      logDataToConsole("Error fetching total document count", error);
      dispatch(setTotalDocuments(0));
      return 0;
    }
  };

export const fetchFinancialMedicalPaOrdersAfterSearch =
  (facilityId: string, providerId: string, searchTerm?: string) =>
  async (dispatch, getState) => {
    const { financialMedicalPaOrders } = getState();
    if (financialMedicalPaOrders.loading) return;

    dispatch(setLoading(true));

    const searchqueryConstraints: QueryConstraint[] = [
      where("assigned_to.facility_id", "==", facilityId),
      where("assigned_to.provider_id", "==", providerId),
      where("status.financial_review", "==", MedicalPaOrderStatus.FINANCIAL),
      orderBy("created_at", "desc"),
    ];

    if (searchTerm) {
      searchqueryConstraints.push(
        where("search_index", "array-contains", searchTerm.toLowerCase()),
      );
    }

    await dispatch(
      fetchTotalDocumentsCount(facilityId, providerId, searchqueryConstraints),
    );

    try {
      const unsubscribe = FirestoreService.fetchQueryCollection<MedicalPaOrder>(
        FirestoreCollectionReference.medicalPaOrders(),
        (data) => {
          dispatch(setFinancialMedicalPaOrders(data as MedicalPaOrder[]));
        },
        (error) => {
          dispatch(setError(error.message));
        },
        ...searchqueryConstraints,
      );

      return unsubscribe;
    } catch (error) {
      dispatch(setError(error as string));
    }
  };

export const fetchFinancialMedicalPaOrders =
  (facilityId: string, pageSize: number, providerId?: string) =>
  async (dispatch, getState) => {
    const { financialMedicalPaOrders } = getState();
    if (financialMedicalPaOrders.loading) return;

    dispatch(setLoading(true));
    dispatch(resetPagination());

    const queryConstraints: QueryConstraint[] = [orderBy("created_at", "desc")];

    try {
      queryConstraints.push(where("assigned_to.facility_id", "==", facilityId));
      if (providerId) {
        queryConstraints.push(
          where("assigned_to.provider_id", "==", providerId),
        );
      }
      queryConstraints.push(
        where("status.financial_review", "==", MedicalPaOrderStatus.FINANCIAL),
      );

      await dispatch(
        fetchTotalDocumentsCount(facilityId, providerId, queryConstraints),
      );

      FirestoreService.paginateQuery<MedicalPaOrder>(
        FirestoreCollectionReference.medicalPaOrders(),
        pageSize,
        ({ docs, lastVisible }) => {
          dispatch(setFinancialMedicalPaOrders(docs));
          dispatch(setLastVisible(lastVisible));
        },
        (error) => {
          dispatch(setError(error.message));
        },
        undefined,
        queryConstraints,
      );
    } catch (error) {
      logDataToConsole("Error in fetchFinancialMedicalPaOrders", error);
      dispatch(setError(error as string));
    }
  };

export const fetchMoreFinancialMedicalPaOrders =
  (facilityId: string, pageSize: number, providerId?: string) =>
  async (dispatch, getState) => {
    const { financialMedicalPaOrders } = getState();

    if (
      financialMedicalPaOrders.loading ||
      financialMedicalPaOrders.showinLineLoader ||
      financialMedicalPaOrders.currentlyLoadedCount ===
        financialMedicalPaOrders.totalDocuments
    ) {
      return;
    }

    dispatch(setinLineLoader(true));

    const queryConstraints: QueryConstraint[] = [orderBy("created_at", "desc")];

    try {
      queryConstraints.push(where("assigned_to.facility_id", "==", facilityId));
      if (providerId) {
        queryConstraints.push(
          where("assigned_to.provider_id", "==", providerId),
        );
      }
      queryConstraints.push(
        where("status.financial_review", "==", MedicalPaOrderStatus.FINANCIAL),
      );

      const unsubscribe = FirestoreService.paginateQuery<MedicalPaOrder>(
        FirestoreCollectionReference.medicalPaOrders(),
        pageSize,
        ({ docs, lastVisible }) => {
          dispatch(appendFinancialMedicalPaOrders(docs));
          dispatch(setLastVisible(lastVisible));
          dispatch(setinLineLoader(false));
        },
        (error) => {
          dispatch(setError(error.message));
          dispatch(setinLineLoader(false));
        },
        financialMedicalPaOrders.lastVisible,
        queryConstraints,
      );

      return unsubscribe;
    } catch (error) {
      dispatch(setError(error as string));
      dispatch(setinLineLoader(false));
    }
  };

export const hasMoreDataToLoad = (state, endIndex: number) => {
  const { financialMedicalPaOrders } = state;

  return (
    financialMedicalPaOrders.currentlyLoadedCount <
      financialMedicalPaOrders.totalDocuments &&
    endIndex === financialMedicalPaOrders.currentlyLoadedCount - 1
  );
};

export default financialMedicalPaOrdersSlice.reducer;
